// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Modal + toast infrastructure and the static modal content (How it works,
 * Privacy / threat model, About). The modal host is hidden with the `hidden`
 * attribute and the global `[hidden]{display:none!important}` guard, so no
 * invisible overlay can ever swallow taps.
 */
import { icon } from './icons.ts';

let hostEl: HTMLElement | null = null;
let lastFocus: HTMLElement | null = null;

export function initModals(): void {
  const host = document.createElement('div');
  host.id = 'modal-host';
  host.hidden = true;
  host.innerHTML = `<div class="modal-backdrop" data-close></div><div class="modal" role="dialog" aria-modal="true"></div>`;
  document.body.appendChild(host);
  hostEl = host;
  host.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t.dataset.close !== undefined || t.closest('[data-close]')) closeModal();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && hostEl && !hostEl.hidden) closeModal();
  });
}

export function openModal(title: string, bodyHtml: string): void {
  if (!hostEl) return;
  lastFocus = document.activeElement as HTMLElement;
  const modal = hostEl.querySelector('.modal') as HTMLElement;
  modal.innerHTML = `
    <div class="modal-head">
      <h2 class="modal-title">${escapeHtml(title)}</h2>
      <button type="button" class="icon-btn" data-close aria-label="Close">${icon('x')}</button>
    </div>
    <div class="modal-body">${bodyHtml}</div>`;
  hostEl.hidden = false;
  const close = modal.querySelector('[data-close]') as HTMLElement | null;
  close?.focus();
}

export function closeModal(): void {
  if (!hostEl) return;
  hostEl.hidden = true;
  const modal = hostEl.querySelector('.modal') as HTMLElement;
  modal.innerHTML = '';
  lastFocus?.focus?.();
}

export function isModalOpen(): boolean {
  return !!hostEl && !hostEl.hidden;
}

let toastTimer: ReturnType<typeof setTimeout> | null = null;
export function toast(msg: string, kind: 'info' | 'ok' | 'err' = 'info'): void {
  let el = document.getElementById('toast');
  if (!el) {
    el = document.createElement('div');
    el.id = 'toast';
    document.body.appendChild(el);
  }
  el.textContent = msg;
  el.dataset.kind = kind;
  el.classList.add('show');
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el?.classList.remove('show'), 2600);
}

function g(term: string, label?: string): string {
  return `<span class="glossary-link" data-term="${escapeHtml(term)}" tabindex="0" role="button">${escapeHtml(label ?? term)}</span>`;
}

export function howItWorksHtml(): string {
  return `
    <ol class="steps">
      <li><span class="step-n">1</span><div><strong>Start a call — or open a link.</strong>
        Starting a call generates a random room id and a four-word ${g('passphrase', 'passphrase')} on your device. Both live only in the link’s <code>#hash</code>, which browsers never send to a server.</div></li>
      <li><span class="step-n">2</span><div><strong>Send the link.</strong>
        Whoever opens it joins the same room. Your browsers find each other through a public ${g('signalling relay', 'signalling relay')} — used once, for the handshake only.</div></li>
      <li><span class="step-n">3</span><div><strong>Browsers connect directly.</strong>
        Using ${g('webrtc', 'WebRTC')} and a public ${g('stun', 'STUN')} helper, each pair of browsers opens a direct path. The handshake is encrypted with your passphrase, so the relay can’t read it or tamper with it.</div></li>
      <li><span class="step-n">4</span><div><strong>The call is ${g('full mesh', 'full mesh')}.</strong>
        Every participant streams voice straight to every other, encoded with ${g('opus', 'Opus')} and encrypted with ${g('dtls-srtp', 'DTLS-SRTP')}. There is no media server — your microphone never passes through one, because there isn’t one to pass through.</div></li>
      <li><span class="step-n">5</span><div><strong>Open mic or ${g('push-to-talk', 'walkie-talkie')}.</strong>
        Open mic is a normal call. Walkie-talkie only transmits while you hold the talk button (or Space) — quiet, private, and easy on a flaky connection.</div></li>
      <li><span class="step-n">6</span><div><strong>Confirm the four words.</strong>
        Both sides see the same ${g('sas', 'four-word code')}. Read them aloud once — if they match, no impostor slipped into the middle.</div></li>
      <li><span class="step-n">7</span><div><strong>Leave with a recording (optional).</strong>
        Hit record and Talkwell mixes every voice into one audio file, encoded on your device. It’s saved to your machine — never uploaded.</div></li>
    </ol>
    <p class="muted small">Mesh calls work best up to about four to six people — every extra person is another direct connection for everyone. For a big room you’d want a server; Talkwell deliberately has none.</p>`;
}

export function privacyHtml(): string {
  return `
    <div class="tm">
      <section><h3 class="tm-good">Protected</h3><ul>
        <li>Your <strong>microphone audio</strong> travels ${g('full mesh', 'directly')} between browsers, ${g('dtls-srtp', 'DTLS-SRTP')}-encrypted. No media server exists in the path — nothing to breach, subpoena or monetise.</li>
        <li>The <strong>${g('passphrase', 'room passphrase')}</strong> is used to encrypt the ${g('signalling relay', 'signalling')} handshake and to derive the ${g('sas', 'four verification words')}. It lives only in the link’s hash and never reaches any server.</li>
        <li>In <strong>${g('push-to-talk', 'walkie-talkie')}</strong> mode your mic transmits nothing until you hold the button — background noise never leaves your device between presses.</li>
        <li><strong>Nothing is recorded</strong> unless you press Record, and a recording is a local file on your device that is never uploaded.</li>
        <li>No account, no sign-in, no cookies, no third-party fonts, no persistent room history.</li>
      </ul></section>
      <section><h3 class="tm-bad">Not protected</h3><ul>
        <li>Peer discovery uses <strong>public nostr relays</strong>. They learn an ephemeral room id and that some browsers are connecting, but the handshake is encrypted with your passphrase — they can’t read the offer/answer or your voice.</li>
        <li>Your <strong>IP address</strong> is visible to the people you connect to and to the public ${g('stun', 'STUN')} server — this is inherent to any peer-to-peer or VoIP call.</li>
        <li>NAT traversal uses public STUN only. A ${g('symmetric nat', 'symmetric-NAT')} pair with no relay may fail to connect. We won’t add a relay because it would be a backend that could see traffic.</li>
        <li>Anyone with the full link (which contains the passphrase) can join. For a tighter flow, share a link <em>without</em> the passphrase and read the four words aloud separately.</li>
      </ul></section>
      <section><h3 class="tm-trust">Trust surface</h3><ul>
        <li>The static site bundle (hash-pinned by the GitHub Pages deploy) and the TLS chain to it.</li>
        <li>The public signalling relays and STUN server (encrypted handshake only; they never carry your call).</li>
        <li>A Cloudflare Web Analytics beacon records anonymous page views — no cookies, no fingerprinting, no cross-site tracking; your files/data are never sent to it.</li>
        <li>Feedback you choose to send is sent to feedback.benrichardson.dev. Nothing is sent unless you open the feedback form and press Send; your files and data never are.</li>
      </ul></section>
    </div>`;
}

export function aboutHtml(): string {
  return `
    <p>Talkwell is a private voice call — and a walkie-talkie — that you start by sending a link. No account, no app, no download, and no server carrying your voice. It runs entirely in your browser.</p>
    <p>It’s part of a small collection of privacy-first, zero-backend browser tools. Everything happens on your device.</p>
    <p class="about-links">
      <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
      <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
      <a href="https://github.com/ben-gy/talkwell" target="_blank" rel="noopener">source on GitHub</a>
    </p>`;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
